import React, { useEffect, useRef } from 'react';

export interface TabItem<T extends string = string> {
  id: T;
  label: React.ReactNode;
  /** Shown as a count pill after the label. */
  count?: number;
  /** attention: the count is work waiting for the user. */
  countTone?: 'attention';
  disabled?: boolean;
  title?: string;
  className?: string;
}

export type TabsVariant = 'folder' | 'segmented' | 'vertical';

export interface TabsProps<T extends string = string> {
  items: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Accessible name for the group. */
  ariaLabel: string;
  /**
   * folder: sections of a page. segmented: another view of the same data.
   * vertical: long forms, settings and filter panels.
   */
  variant?: TabsVariant;
  size?: 'md' | 'sm';
  /** Share the full width equally, as on a sign-in card. */
  fill?: boolean;
  /** Pairs each tab with a <TabPanel> using the same base. */
  idBase?: string;
  className?: string;
}

export const tabId = (idBase: string, id: string) => `${idBase}-tab-${id}`;
export const tabPanelId = (idBase: string, id: string) => `${idBase}-panel-${id}`;

/**
 * Tabs with the keyboard behaviour the ARIA tabs pattern expects: one tab stop
 * for the group, arrow keys to move between tabs, Home and End to jump.
 */
const Tabs = <T extends string = string>({
  items,
  value,
  onChange,
  ariaLabel,
  variant = 'folder',
  size = 'md',
  fill = false,
  idBase,
  className,
}: TabsProps<T>) => {
  const listRef = useRef<HTMLDivElement>(null);
  const vertical = variant === 'vertical';

  // Keep the selected tab visible when the row scrolls sideways on a phone.
  // Guarded: jsdom has no scrollIntoView.
  useEffect(() => {
    const selected = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    selected?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [value]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const enabled = items.filter((item) => !item.disabled);
    const index = enabled.findIndex((item) => item.id === value);
    const last = enabled.length - 1;
    const nextKey = vertical ? 'ArrowDown' : 'ArrowRight';
    const prevKey = vertical ? 'ArrowUp' : 'ArrowLeft';
    let next: number;
    if (event.key === nextKey) next = index >= last ? 0 : index + 1;
    else if (event.key === prevKey) next = index <= 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;
    event.preventDefault();
    const target = enabled[next];
    if (!target) return;
    onChange(target.id);
    listRef.current
      ?.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(target.id)}"]`)
      ?.focus();
  };

  // If nothing matches, the first enabled tab keeps the group reachable by Tab.
  const tabStop = items.some((item) => item.id === value && !item.disabled)
    ? value
    : items.find((item) => !item.disabled)?.id;

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation={vertical ? 'vertical' : undefined}
      className={[
        'ui-tabs',
        `ui-tabs--${variant}`,
        size === 'sm' ? 'ui-tabs--sm' : '',
        fill ? 'ui-tabs--fill' : '',
        className || '',
      ]
        .filter(Boolean)
        .join(' ')}
      onKeyDown={onKeyDown}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          data-tab-id={item.id}
          id={idBase ? tabId(idBase, item.id) : undefined}
          aria-controls={idBase ? tabPanelId(idBase, item.id) : undefined}
          aria-selected={item.id === value}
          tabIndex={item.id === tabStop ? 0 : -1}
          disabled={item.disabled}
          title={item.title}
          className={['ui-tab', item.className || ''].filter(Boolean).join(' ')}
          onClick={() => onChange(item.id)}
        >
          {item.label}
          {item.count !== undefined && (
            <>
              {' '}
              <span
                className={[
                  'ui-count',
                  item.countTone === 'attention' ? 'ui-count--attention' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {item.count.toLocaleString()}
              </span>
            </>
          )}
        </button>
      ))}
    </div>
  );
};

export default Tabs;
